import React from 'react';
import { Brain, Check, CornerDownRight, FileText, Users, Sparkles, ShieldCheck } from 'lucide-react';
import { SCENARIOS, FOCUSED_IDS } from './scenarios';

const glass = {
  background: 'linear-gradient(180deg, rgba(17,23,40,0.82), rgba(10,14,26,0.82))',
  border: '1px solid rgba(255,255,255,0.10)', backdropFilter: 'blur(14px)',
  boxShadow: '0 30px 80px -30px rgba(0,0,0,0.8), inset 0 1px 0 rgba(255,255,255,0.06)',
};

function AstridRow({ children }) {
  return (
    <div className="flex items-start gap-2.5">
      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand/15 text-brand ring-1 ring-brand/30"><Brain className="h-4 w-4" /></span>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

function Btns({ items }) {
  return (
    <div className="mt-3 flex gap-2">
      {items.map((b, i) => (
        <button key={b} disabled className={`flex-1 rounded-full px-3 py-2 text-xs font-bold ${i === items.length - 1 ? 'bg-brand text-white' : 'border border-white/15 text-slate-200'}`}
          style={i === items.length - 1 ? { animation: 'astridPulse 1.4s ease-in-out infinite' } : {}}>{b}</button>
      ))}
    </div>
  );
}

function Card({ scn, done }) {
  const c = done ? scn.done : scn.card;
  if (c.type === 'insight') return (
    <>
      <div className="rounded-2xl rounded-tl-sm border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-slate-100">
        <div className="font-semibold text-white">{c.title.replace(/18\.4%/, '')}<span className="text-brand">18.4%</span> this month.</div>
        <ul className="mt-2 space-y-1 text-[13px] text-slate-300">
          {c.bullets.map((b) => <li key={b} className="flex gap-2"><CornerDownRight className="mt-0.5 h-3.5 w-3.5 text-brand/80" />{b}</li>)}
        </ul>
      </div>
      <button disabled className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-brand/40 bg-brand/10 px-3.5 py-1.5 text-xs font-semibold text-brand">{c.cta}</button>
    </>
  );
  if (c.type === 'setup') return (
    <>
      <div className="text-sm text-slate-200">{c.line}</div>
      <div className="mt-2.5 rounded-xl border border-white/10 bg-white/[0.05] p-3.5">
        <div className="flex items-center gap-2 text-[13px] font-semibold text-white"><FileText className="h-4 w-4 text-brand" /> {c.title}</div>
        <div className="mt-2 flex items-baseline gap-2"><span className="text-2xl font-black text-white">{c.metric}</span><span className="text-[11px] text-slate-400">{c.metricLabel}</span></div>
        <div className="mt-2 space-y-1">{c.rows.map((r) => <div key={r} className="flex items-center gap-1.5 text-[11px] text-slate-300"><Check className="h-3 w-3 text-emerald-400" />{r}</div>)}</div>
        <Btns items={c.buttons} />
      </div>
    </>
  );
  if (c.type === 'approval') return (
    <>
      <div className="text-sm leading-relaxed text-slate-200">{c.line}</div>
      <div className="mt-2.5 rounded-xl border border-white/10 bg-white/[0.05] p-3.5">
        <div className="grid grid-cols-3 gap-2 text-center">
          {c.stats.map(([n, l]) => <div key={l}><div className="text-lg font-black text-white">{n}</div><div className="text-[10px] uppercase tracking-wide text-slate-400">{l}</div></div>)}
        </div>
        <div className="mt-2 flex items-center gap-1.5 rounded-lg bg-amber-400/10 px-2 py-1 text-[10px] font-semibold text-amber-300"><ShieldCheck className="h-3 w-3" /> Nothing sent until you approve</div>
        <Btns items={c.buttons} />
      </div>
    </>
  );
  if (c.type === 'completion') return (
    <div className="rounded-2xl rounded-tl-sm border border-emerald-400/20 bg-emerald-400/[0.06] px-4 py-3 text-sm text-slate-100">
      <div className="flex items-center gap-1.5 font-semibold text-emerald-300"><Check className="h-4 w-4" /> {c.title}</div>
      <p className="mt-1 text-[13px] text-slate-300">{c.line}</p>
    </div>
  );
  if (c.type === 'team') return (
    <>
      <div className="text-sm text-slate-200">{c.line}</div>
      <div className="mt-2.5 rounded-xl border border-white/10 bg-white/[0.05] p-3.5">
        <div className="flex items-center gap-2 text-[13px] font-semibold text-white"><Users className="h-4 w-4 text-brand" /> {c.title}</div>
        <div className="mt-2.5 flex gap-2">
          {c.people.map(([name, work]) => (
            <div key={name} className="flex-1 rounded-lg border border-white/10 bg-white/[0.04] p-2 text-center">
              <div className="mx-auto mb-1 flex h-7 w-7 items-center justify-center rounded-full bg-brand/20 text-[11px] font-bold text-brand">{name[0]}</div>
              <div className="text-[11px] font-semibold text-white">{name}</div>
              <div className="text-[10px] text-slate-400">{work}</div>
            </div>
          ))}
        </div>
        <div className="mt-2.5 border-t border-white/10 pt-2 text-[11px] text-slate-400">{c.summary}</div>
      </div>
    </>
  );
  return null;
}

export default function ConversationOverlay({ scn, id }) {
  const scenario = SCENARIOS[scn];
  const focused = FOCUSED_IDS.has(id);
  const showPresent = id === 'present' || (id === 'hold' && !scenario.done);
  const showDone = id === 'done' || (id === 'hold' && scenario.done);

  return (
    <div className="relative w-full" data-testid="astrid-conversation"
      style={{ transition: 'transform 0.8s cubic-bezier(0.16,1,0.3,1), filter 0.8s, opacity 0.8s',
        transform: focused ? 'scale(1)' : 'translateY(-12px) scale(1.05)', filter: focused ? 'blur(0)' : 'blur(7px)', opacity: focused ? 1 : 0.16 }}>
      <div className="overflow-hidden rounded-2xl" style={glass}>
        <div className="flex items-center gap-2 border-b border-white/10 px-4 py-2.5">
          <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-brand text-white"><Brain className="h-3.5 w-3.5" /></span>
          <span className="text-xs font-bold text-white">Astrid</span>
          <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-semibold text-slate-200"><Sparkles className="h-3 w-3 text-brand" /> AI teammate · {scenario.tag}</span>
        </div>
        <div className="flex min-h-[300px] flex-col gap-4 px-4 py-4 sm:min-h-[330px]">
          <div className="flex justify-end">
            <div className="max-w-[86%] rounded-2xl rounded-tr-sm bg-brand px-4 py-2.5 text-sm font-medium text-white shadow-lg shadow-brand/25">{scenario.intent}</div>
          </div>
          {showPresent && <AstridRow><Card scn={scenario} /></AstridRow>}
          {showDone && <AstridRow><Card scn={scenario} done /></AstridRow>}
        </div>
      </div>
      {!focused && (
        <div className="pointer-events-none absolute -bottom-9 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full border border-white/10 bg-black/60 px-3.5 py-1.5 text-[11px] font-medium text-slate-200 backdrop-blur">
          <span className="mr-1.5 inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-brand" />
          {id === 'execute' ? 'Sending approved requests · 83 / 83' : scenario.status}
        </div>
      )}
    </div>
  );
}
